import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActionMenu, Alert, Avatar, Badge, Breadcrumb, Button, Card, Input, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { openSlotSidebar } from './openSlotSidebar';
import { useMe } from '../../auth/MeProvider';
import { apiDelete, apiGet } from '../../lib/api';
import { LOCATION_LABELS, bookingUrl, landingUrl, formatAvailability, type AvailabilitySummary } from '../../openslot/helpers';

interface EventTypeSummary {
  id: string;
  title: string;
  slug: string;
  durationMin: number;
  durations: number[];
  locationType: string;
  color: string;
  isActive: boolean;
  availability?: AvailabilitySummary;
}

/** Our scheduling events are always one-to-one (no group/collective events yet). */
const EVENT_KIND_LABEL = 'One-on-One';

export function EventTypesPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useMe();
  const [list, setList] = useState<EventTypeSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    apiGet<EventTypeSummary[]>('/api/openslot/event-types').then(setList).catch(() => setErr('Failed to load.'));
  }, []);
  useEffect(load, [load]);

  const copyLink = (et: EventTypeSummary) => {
    void navigator.clipboard?.writeText(bookingUrl(et.slug));
    setCopied(et.id);
    setTimeout(() => setCopied(null), 1500);
  };

  const remove = async (ids: string[]) => {
    const many = ids.length > 1;
    if (!confirm(`Delete ${many ? `${ids.length} scheduling events` : 'this scheduling event'}? Existing bookings are kept.`)) return;
    await Promise.all(ids.map((id) => apiDelete(`/api/openslot/event-types/${id}`)));
    setSelected(new Set());
    load();
  };

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const filtered = useMemo(() => {
    if (!list) return null;
    const q = query.trim().toLowerCase();
    return q ? list.filter((et) => et.title.toLowerCase().includes(q)) : list;
  }, [list, query]);

  const ownerName = me?.displayName ?? me?.email ?? 'You';
  const ownerInitials = (me?.displayName ?? me?.email ?? '?').slice(0, 2);

  const durationLabel = (et: EventTypeSummary) =>
    et.durations.length > 1 ? `${et.durations.length} durations` : `${et.durationMin} min`;

  return (
    <AppChrome secondarySidebar={openSlotSidebar(navigate, pathname)}>
      <PageHeader
        title={<>Open Slot - <span className="fw-normal">Scheduling</span></>}
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Open Slot' }, { label: 'Scheduling' }]} />}
        actions={
          <Button variant="primary" icon="ph-plus" onClick={() => navigate('/openslot/scheduling/new')}>
            New scheduling event
          </Button>
        }
      />
      <div className="content">
        {err && <Alert variant="danger" icon="ph-warning">{err}</Alert>}

        {/* Section tabs — only Event types is implemented today. */}
        <ul className="nav nav-tabs nav-tabs-highlight mb-3">
          <li className="nav-item">
            <span className="nav-link active fw-semibold">Event types</span>
          </li>
          <li className="nav-item">
            <span className="nav-link disabled" title="Coming soon">Single-use links</span>
          </li>
          <li className="nav-item">
            <span className="nav-link disabled" title="Coming soon">Meeting polls</span>
          </li>
        </ul>

        {/* Search */}
        <div className="position-relative mb-3">
          <i className="ph-magnifying-glass position-absolute text-muted" style={{ left: 14, top: '50%', transform: 'translateY(-50%)' }} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search event types"
            className="ps-5"
          />
        </div>

        {/* Owner header + landing link */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <Avatar size={32} initials={ownerInitials} />
            <span className="fw-semibold ms-2">{ownerName}</span>
          </div>
          <a
            href={landingUrl()}
            target="_blank"
            rel="noreferrer"
            className="d-inline-flex align-items-center text-primary text-decoration-none fw-semibold"
          >
            <i className="ph-arrow-square-out me-2" />
            View landing page
          </a>
        </div>

        {/* Bulk-selection toolbar */}
        {selected.size > 0 && (
          <div className="d-flex align-items-center justify-content-between bg-light rounded border p-2 px-3 mb-3">
            <span className="fw-semibold">{selected.size} selected</span>
            <div className="d-flex gap-2">
              <Button variant="light" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
              <Button variant="danger" size="sm" icon="ph-trash" onClick={() => remove([...selected])}>Delete</Button>
            </div>
          </div>
        )}

        {!filtered && <Spinner />}

        {filtered && filtered.length === 0 && (
          <Card title={query ? 'No matching event types' : 'No scheduling events yet'}>
            <p className="mb-0 text-muted">
              {query
                ? 'Try a different search.'
                : 'Create your first bookable scheduling event to get a shareable link.'}
            </p>
          </Card>
        )}

        <div className="d-flex flex-column gap-2">
          {filtered?.map((et) => (
            <div
              key={et.id}
              className="card mb-0"
              style={{ borderLeft: `4px solid ${et.color}` }}
            >
              <div className="card-body d-flex align-items-center">
                <input
                  type="checkbox"
                  className="form-check-input mt-0 flex-shrink-0"
                  checked={selected.has(et.id)}
                  onChange={() => toggleSelect(et.id)}
                  aria-label={`Select ${et.title}`}
                />

                <div className="flex-grow-1 ms-3 me-3" style={{ minWidth: 0 }}>
                  <div className="d-flex align-items-center mb-1">
                    <h6 className="fw-bold mb-0 text-truncate">{et.title}</h6>
                    {!et.isActive && <Badge variant="secondary" soft className="ms-2 flex-shrink-0">Inactive</Badge>}
                  </div>
                  <div className="text-muted fs-sm">
                    {durationLabel(et)} • {LOCATION_LABELS[et.locationType] ?? et.locationType} • {EVENT_KIND_LABEL}
                  </div>
                  <div className="text-muted fs-sm">{formatAvailability(et.availability)}</div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <Button variant="primary" outline size="sm" icon="ph-link" onClick={() => copyLink(et)}>
                    {copied === et.id ? 'Copied!' : 'Copy link'}
                  </Button>
                  <a
                    href={bookingUrl(et.slug)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-light btn-icon btn-sm"
                    title="Open booking page"
                    aria-label="Open booking page"
                  >
                    <i className="ph-arrow-square-out" />
                  </a>
                  <ActionMenu
                    items={[
                      { label: 'Edit', icon: 'ph-pencil', onClick: () => navigate(`/openslot/scheduling/${et.id}`) },
                      { label: 'Copy link', icon: 'ph-link', onClick: () => copyLink(et) },
                      { label: 'View booking page', icon: 'ph-arrow-square-out', onClick: () => window.open(bookingUrl(et.slug), '_blank') },
                      { label: 'Delete', icon: 'ph-trash', danger: true, divider: true, onClick: () => remove([et.id]) },
                    ]}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppChrome>
  );
}
